// Helper for WhatsApp click-to-chat via wa.me
// REMINDER: Phone numbers must be in international format without '+' or spaces.
import { toast } from "sonner";

export function normalizePhone(phone) {
  if (!phone) return "";
  return phone.replace(/\D/g, "");
}

/**
 * Convert markdown to WhatsApp-safe formatting.
 * - WhatsApp uses *bold*, _italic_, ~strike~, ```mono```
 * - Strips ##/###/#### headings (keeps text as bold)
 * - Strips horizontal rules (--- or ***)
 * - Converts **bold** → *bold* (single-asterisk WhatsApp bold)
 * - Drops bullet markers `- ` / `* ` and renders as `• `
 * - Removes weird non-printable / variation-selector chars that render as "?"
 * - Collapses 3+ blank lines to 2
 */
export function markdownToWhatsApp(text) {
  if (!text) return "";
  let s = String(text);

  // Strip variation-selectors / ZWJ noise that often shows as "?" / □
  s = s.replace(/[\uFE0E\uFE0F\u200B-\u200D\uFEFF]/g, "");

  const lines = s.split(/\r?\n/);
  const out = [];
  for (let raw of lines) {
    let line = raw;
    // Remove horizontal rules
    if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      out.push("");
      continue;
    }
    // Headings → bold (WhatsApp uses single *)
    const h = line.match(/^\s*#{1,6}\s+(.*)$/);
    if (h) {
      const t = h[1].replace(/[*_`]+/g, "").trim();
      out.push(t ? `*${t}*` : "");
      continue;
    }
    // Bullets - `- foo` / `* foo` / `+ foo` → `• foo`
    line = line.replace(/^(\s*)[-*+]\s+/, "$1• ");
    // Numbered lists already look fine, leave them
    // Convert **bold** → *bold*
    line = line.replace(/\*\*(.+?)\*\*/g, "*$1*");
    // Convert __bold__ → *bold*
    line = line.replace(/__(.+?)__/g, "*$1*");
    // Strip inline code ticks (keep content)
    line = line.replace(/`([^`]+)`/g, "$1");
    out.push(line);
  }
  // Collapse 3+ consecutive empty lines → max 2
  let joined = out.join("\n").replace(/\n{3,}/g, "\n\n");
  return joined.trim();
}

export function waLink(phone, text) {
  const p = normalizePhone(phone);
  const cleaned = markdownToWhatsApp(text || "");
  const encoded = encodeURIComponent(cleaned);
  // wa.me redirects desktop browsers to api.whatsapp.com which is blocked by some
  // corporate networks / sandboxed environments. Detect device and route around it:
  //   • Mobile  → wa.me (deep-links to the WhatsApp app, best native UX)
  //   • Desktop → web.whatsapp.com directly (the user's existing WhatsApp Web tab)
  const isMobile =
    typeof navigator !== "undefined" &&
    /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  if (isMobile) {
    return `https://wa.me/${p}?text=${encoded}`;
  }
  return `https://web.whatsapp.com/send/?phone=${p}&text=${encoded}&type=phone_number&app_absent=0`;
}

// Always opens WhatsApp in a NEW tab. Never navigates the current tab.
// Strategy:
//   1. window.open(url, "_blank") within the user-gesture click handler - most
//      reliable across browsers and the only thing that survives strict popup
//      blockers when invoked synchronously from a click.
//   2. Synthetic <a target="_blank"> click as a backup (helps on some mobile
//      WebViews where window.open returns null but anchors still navigate).
//   3. Last resort: copy URL to clipboard and toast so the user can paste it.
export function openWhatsApp(phone, text) {
  const url = waLink(phone, text);
  // 1. Direct window.open
  try {
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (win && !win.closed && typeof win.focus === "function") {
      try { win.focus(); } catch (_) {}
      return;
    }
    if (win) return; // some browsers return a non-null wrapped window
  } catch (_) { /* fall through */ }
  // 2. Synthetic anchor click
  try {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  } catch (_) { /* fall through */ }
  // 3. Clipboard fallback
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url);
      toast.message("Popup blocked - WhatsApp link copied. Paste into a new tab.");
      return;
    }
  } catch (_) { /* fall through */ }
  // 4. Final fallback - prompt the user with the link
  try {
    window.prompt("Copy this WhatsApp link and open it manually:", url);
  } catch (_) {
    toast.error("Could not open WhatsApp.");
  }
}

export const templates = {
  welcome: (name, gymName = "our gym") =>
    `Hey ${name}! Thanks for your interest in ${gymName}. When would you like to come in for a free trial?`,
  follow_up: (name) =>
    `Hi ${name}, just circling back - are you free this week to drop by for a trial session?`,
  trial_reminder: (name, when) =>
    `Hey ${name}! Quick reminder - your trial is on ${when}. We're excited to see you.`,
  trial_feedback: (name) =>
    `Hi ${name}, hope you enjoyed your trial! Any questions? We'd love to have you join the family.`,
  renewal: (name, days) =>
    `Hey ${name}! Your membership expires in ${days} day${days === 1 ? "" : "s"}. Want to keep training? Let's renew.`,
  inactivity: (name) =>
    `Hey ${name}! Missed you at the gym lately. Everything okay? We're here whenever you're ready.`,
  custom: () => "",
};
